import React from "react";
// Customizable Area Start
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    CircularProgress,
    Grid,
    Avatar,
    Switch,
    FormControlLabel,
    Select,
    MenuItem
} from "@material-ui/core";
import {
    ThemeProvider,
    withStyles,
    createStyles
} from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import * as yup from "yup";
import { lockIcon } from "../../email-account-registration/src/assets";
import { ErrorMessage, Formik } from "formik";
import CustomBackButton from "../../../components/src/CustomBackButton.web";
import {
    handleAndConditions,
    handleOrConditions
} from "../../../components/src/CommonFunctions";
import GuestSignupController, { Props } from "./GuestSignupController.web";
import {
    commonStyles,
    themeWhite,
    themeYellow
} from "../../../framework/src/enduserTheme";

const configJSON = require("./config");

// Customizable Area End

const modalStyle = {
    position: "absolute" as "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    outline: "none",
    display: "flex",
    flexDirection: "column" as "column",
    padding: "20px",
    boxSizing: "border-box" as "border-box",
    fontFamily: "Inter, 'sans-serif'",
    overflowY: "auto" as "auto"
};

const validation_Schema_stepOne = yup.object({
    thoughts: yup
        .string()
        .required("Your thoughts are required")
        .test("max-words", "The shared thoughts cannot have more than 40 words", value => {
            const wordCount = value ? value.trim().split(/\s+/).length : 0;
            return wordCount <= 40;
        })
});

const validation_Schema_stepTwo = yup.object({
    userName: yup
        .string()
        .required("Full name is required")
        .matches(/^[a-zA-Z]{2,}( [a-zA-Z]{2,})+$/, "Please enter a valid full name")
        .matches(
            /^[a-zA-Z ]*$/,
            "Name cannot contain special characters or numbers"
        )
});

const validation_Schema_stepThree = yup.object({
    phoneNumber: yup
        .string()
        .required("Phone number is required")
        .min(10, "Must be at least 10 digits")
});

const IOSSwitch = withStyles(theme => ({
    root: {
        width: 46,
        height: 26,
        padding: 0,
        margin: theme.spacing(1)
    },
    switchBase: {
        padding: 1,
        color: "#FFC629",
        "&$checked": {
            transform: "translateX(20px)",
            color: theme.palette.common.white,
            backgroundColor: "#FFC629",
            "& + $track": {
                backgroundColor: "#FFC629",
                opacity: 1,
                border: "1px solid #000"
            }
        },
        "&$focusVisible $thumb": {
            color: theme.palette.common.white,
            border: "6px solid #fff"
        }
    },
    thumb: {
        width: 24,
        height: 24
    },
    track: {
        borderRadius: 26 / 2,
        border: `1px solid #000`,
        backgroundColor: theme.palette.grey[50],
        opacity: 1,
        transition: theme.transitions.create(["background-color", "border"])
    },
    checked: {},
    focusVisible: {}
    // @ts-ignore
}))(({ classes, ...props }) => {
    return (
        <Switch
            data-test-id="switchBtn"
            focusVisibleClassName={classes.focusVisible}
            disableRipple
            classes={{
                root: classes.root,
                switchBase: classes.switchBase,
                thumb: classes.thumb,
                track: classes.track,
                checked: classes.checked
            }}
            {...props}
        />
    );
});

class GuestSignup extends GuestSignupController {
    inputRefs: any[];

    constructor(props: Props) {
        super(props);
        this.inputRefs = Array(6)
            .fill(null)
            .map(() => React.createRef());
    }

    handleOtpKeyDown = (event: any, index: number) => {
        if (event.key === "Backspace") {
            if (index > 0 && !this.state.otp[index]) {
                this.inputRefs[index - 1].current.focus();
                this.handleOtpChange("", index - 1);
            }
        }
    };

    handleOtpInput = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const value = event.target.value;
        if (/^[0-9]$/.test(value) || value === "") {
            this.handleOtpChange(value, index);
            if (value && index < 5) {
                this.inputRefs[index + 1].current.focus();
            }
        }
    };

    render() {
        // @ts-ignore
        const { classes } = this.props;

        const StepTwo = () => {
            return (
                <Formik
                    initialValues={{ userName: this.state.userName }}
                    validationSchema={validation_Schema_stepTwo}
                    onSubmit={values => this.handleNameSubmit(values)}
                >
                    {({ errors, touched, handleChange, handleBlur, values, handleSubmit }) => (
                        <>
                            <Box className={classes.backIconBox}>
                                <CustomBackButton handleClick={this.redirectToFirstPage} />
                            </Box>
                            <Box className={classes.secondStepMainBox}>
                                <Typography align="center" className={classes.centerTextSU1}>
                                    {this.state.thoughts}
                                </Typography>
                            </Box>
                            <Box className={classes.addNameBox}>
                                <Typography className={classes.textTwoSU}>
                                    {configJSON.addPersonalTouchMessage}
                                </Typography>
                                <Typography className={classes.textOne}>
                                    {configJSON.whatIsFullNameLabel}
                                </Typography>
                                <input
                                    name="userName"
                                    placeholder=" Add Your Name"
                                    className={classes.nameInputSU}
                                    value={values.userName}
                                    onChange={handleChange("userName")}
                                    onBlur={handleBlur("userName")}
                                />
                                {handleAndConditions(
                                    touched.userName,
                                    errors.userName,
                                    <Typography className={classes.errorMsg}>
                                        {errors.userName}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                onClick={() => handleSubmit()}
                                className={handleOrConditions(
                                    values.userName,
                                    classes.buttonPrimary,
                                    handleOrConditions(this.state.themeSwitch, classes.buttonYellowDisable, classes.buttonDisable)
                                )}
                            >
                                {configJSON.continueLabel}
                            </Button>
                        </>
                    )}
                </Formik>
            );
        };

        const StepThree = () => {
            return (
                <Formik
                    initialValues={{ code: "US", phoneNumber: "" }}
                    validationSchema={validation_Schema_stepThree}
                    onSubmit={values => this.senOtpToUser(values)}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <>
                            <Box className={classes.backIconBox}>
                                <CustomBackButton handleClick={this.redirectToSecondPage} />
                            </Box>
                            <Typography align="center" className={classes.centerTextSU1}>
                                {this.state.thoughts}
                            </Typography>
                            <Box>
                                <Typography className={classes.lockTextSU}>
                                    {configJSON.securityMessage}
                                </Typography>
                                <Box className={classes.flexDisplaySU}>
                                    <Box className={classes.marginAutoSU}>
                                        <Select
                                            name="code"
                                            disableUnderline
                                            className={classes.countryCodeSelect}
                                            renderValue={(value: unknown) => (
                                                <>
                                                    <span
                                                        className={`country-flag-outer fi fi-${(value as string).toLocaleLowerCase()}`}
                                                    />
                                                    {this.getOuterCountryLabel(value as string)}
                                                </>
                                            )}
                                            value={values.code}
                                            onChange={handleChange}
                                        >
                                            {configJSON.countryCodes?.map((countryInfo: any) => (
                                                <MenuItem key={countryInfo.code} value={countryInfo.code}>
                                                    <span className={`country-flag fi fi-${countryInfo.code.toLowerCase()}`} />
                                                    <span className="country-name">{countryInfo.label.split(" ")[0]}</span>
                                                    <span className="country-dial">{countryInfo.label.split(" ").slice(1).join(" ")}</span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Box className={classes.phoneNumberWrapper}>
                                            <Box className={classes.separator} />
                                            <TextField
                                                name="phoneNumber"
                                                className={classes.phoneNumber}
                                                value={values.phoneNumber}
                                                onChange={handleChange}
                                                onKeyPress={this.handleKeyPress}
                                                placeholder="Add Your Mobile Number"
                                                InputProps={{ disableUnderline: true }}
                                                inputProps={{ maxLength: 10 }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                <ErrorMessage component="p" name="phoneNumber" className={classes.errorMsg} />
                                <Box className={classes.flexDisplaySU}>
                                    <img src={lockIcon} alt="lockIcon" className={classes.lockIcon} />
                                    <Typography className={classes.lockTextSU}>
                                        {configJSON.protectCommunityMessage}
                                    </Typography>
                                </Box>
                                {handleOrConditions(
                                    this.state.phoneNumberError,
                                    <Typography className={classes.errorMsg}>
                                        {configJSON.phoneNumberErrorMessage}
                                    </Typography>,
                                    null
                                )}
                            </Box>
                            <Box>
                                <Button
                                    onClick={() => handleSubmit()}
                                    className={handleOrConditions(
                                        values.phoneNumber,
                                        classes.buttonPrimaryThree,
                                        handleOrConditions(this.state.themeSwitch, classes.buttonYellowDisable, classes.buttonDisable)
                                    )}
                                >
                                    {configJSON.continueLabel}
                                </Button>
                                <Typography className={classes.lockTextSU} style={{ marginTop: '10px' }}>
                                    {configJSON.termsConditionsMessage}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Formik>
            );
        };

        const StepFour = () => {
            return (
                <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Box className={classes.backIconBox}>
                        <CustomBackButton handleClick={this.redirectToThirdPage} />
                    </Box>
                    <Typography align="center" className={classes.centerTextSU1}>
                        {this.state.thoughts}
                    </Typography>
                    <Box style={{ marginTop: "40px", textAlign: "center" }}>
                        <Typography style={{ fontSize: "12px", fontWeight: 400, marginBottom: "24px", textAlign: "left" }}>
                            Enter the code we sent to {this.state.userPhoneNumber}
                        </Typography>
                        <Grid container spacing={1} justifyContent="center" style={{ marginBottom: 24 }}>
                            {this.state.otp.map((digit, index) => (
                                <Grid item key={index}>
                                    <TextField
                                        inputRef={this.inputRefs[index]}
                                        value={digit}
                                        onChange={e => this.handleOtpInput(e as React.ChangeEvent<HTMLInputElement>, index)}
                                        onKeyDown={e => this.handleOtpKeyDown(e, index)}
                                        variant="outlined"
                                        inputProps={{
                                            maxLength: 1,
                                            style: {
                                                textAlign: "center",
                                                width: 40,
                                                height: 40,
                                                padding: 0,
                                                fontSize: "20px",
                                                fontWeight: 700,
                                                backgroundColor: "#FFF",
                                                borderRadius: 8
                                            }
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                        {this.state.error && <Typography color="error">{this.state.error}</Typography>}
                        <Button onClick={() => this.redirectToThirdPage()} style={{ textTransform: "none", color: "#000", marginBottom: 24, fontWeight: 700 }}>
                            Change the phone number
                        </Button>
                        <Button style={{ textTransform: "none", color: "#000", marginBottom: 20, fontWeight: 700 }} onClick={() => this.senOtpToUser({ code: "US", phoneNumber: this.state.userPhoneNumber.slice(-10) })}>
                            Resend Code
                        </Button>
                    </Box>
                    <Button
                        fullWidth
                        variant="contained"
                        style={{
                            backgroundColor: !this.state.otp.includes("") ? "#000" : "rgba(0,0,0,0.2)",
                            color: "#FFF",
                            fontWeight: "bold",
                            height: 56,
                            borderRadius: 28,
                            marginTop: "auto",
                            marginBottom: 20
                        }}
                        onClick={() => this.verifyOtp()}
                        disabled={this.state.otp.includes("") || this.state.isLoading}
                    >
                        {this.state.isLoading ? <CircularProgress size={24} color="inherit" /> : "Continue"}
                    </Button>
                </Box>
            );
        };

        const StepFive = () => {
            return (
                <Box style={{ flex: 1, display: "flex", flexDirection: "column", backgroundColor: "#FFF", margin: "-20px", padding: "20px" }}>
                    <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <Typography style={{ fontWeight: 700, fontSize: "16px", marginBottom: "40px" }}>
                            {this.state.userName}
                        </Typography>
                        <Typography align="center" className={classes.centerTextSU1}>
                            {this.state.thoughts}
                        </Typography>
                        <Box style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginTop: "20px" }}>
                            <Avatar style={{ width: 44, height: 44, border: "1px solid #000" }} />
                            <Box>
                                <Typography style={{ fontWeight: 700, fontSize: "15px", marginBottom: "4px" }}>
                                    {this.state.userName}
                                </Typography>
                                <Typography style={{ fontSize: "13px", color: "rgba(0,0,0,0.6)", lineHeight: 1.4 }}>
                                    Congratulations! Your profile has been created. Click below to listen to your voice and help our community see you join.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Button
                        fullWidth
                        variant="contained"
                        style={{ backgroundColor: "#000", color: "#FFF", fontWeight: "bold", height: 56, borderRadius: 28, marginBottom: 20, textTransform: "none" }}
                        onClick={() => this.createPost()}
                    >
                        Post
                    </Button>
                </Box>
            );
        };

        return (
            <ThemeProvider theme={this.state.themeSwitch ? themeYellow : themeWhite}>
                <CssBaseline />
                <Modal open={true} onClose={this.props.onClose} aria-labelledby="guest-signup-modal">
                    <Box style={{
                        ...modalStyle,
                        backgroundColor: this.state.themeSwitch ? "#FFC629" : "#FFFFFF"
                    }}>
                        {this.state.stepOne && (
                            <Formik
                                initialValues={{ thoughts: this.state.thoughts }}
                                validationSchema={validation_Schema_stepOne}
                                onSubmit={values => this.handleThoughtSubmit(values)}
                            >
                                {({ errors, touched, handleChange, handleBlur, values, handleSubmit }) => (
                                    <>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <CustomBackButton
                                                data-test-id="backToStepOne"
                                                handleClick={this.props.onClose}
                                            />
                                            <Box>
                                                <FormControlLabel
                                                    control={
                                                        <IOSSwitch
                                                            // @ts-ignore
                                                            checked={this.state.themeSwitch}
                                                            onChange={this.handelThemeChange}
                                                            name="checkedB"
                                                        />
                                                    }
                                                    label=""
                                                />
                                            </Box>
                                        </Box>
                                        <Box style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                            <textarea
                                                rows={4}
                                                name="thoughts"
                                                className={classes.thoughtsInput}
                                                placeholder={configJSON.enterThoughtsPlaceholder}
                                                value={values.thoughts}
                                                onChange={handleChange("thoughts")}
                                                onBlur={handleBlur("thoughts")}
                                            />
                                            {handleAndConditions(
                                                touched.thoughts,
                                                errors.thoughts,
                                                <Typography className={classes.errorMsg} style={{ textAlign: 'center' }}>
                                                    {errors.thoughts}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Button
                                            onClick={() => handleSubmit()}
                                            className={handleOrConditions(
                                                values.thoughts,
                                                classes.buttonPrimary,
                                                handleOrConditions(this.state.themeSwitch, classes.buttonYellowDisable, classes.buttonDisable)
                                            )}
                                        >
                                            {configJSON.continueLabel}
                                        </Button>
                                    </>
                                )}
                            </Formik>
                        )}
                        {this.state.stepTwo && <StepTwo />}
                        {this.state.stepThree && <StepThree />}
                        {this.state.stepFour && <StepFour />}
                        {this.state.stepFive && <StepFive />}
                    </Box>
                </Modal>
            </ThemeProvider>
        );
    }
}

const styles = () =>
    createStyles({
        addNameBox: {
            width: "100%",
            display: "flex",
            flexDirection: "column"
        },
        iconContainer: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
        },
        errorMsg: {
            ...commonStyles.errorMsg,
            margin: "3px 0px 0px"
        },
        nameInputSU: {
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "30px",
            outline: "none",
            background: "transparent",
            padding: "5px 0px",
            margin: "10px 0 4px",
            border: "none",
            fontFamily: "Inter, 'sans-serif'",
            borderBottom: "1px solid #000",
            "&::placeholder": {
                color: "#7f6415",
                fontFamily: "Inter, 'sans-serif'"
            }
        },
        marginAutoSU: {
            marginTop: "10px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            "& .country-flag-outer": {
                marginRight: "5px",
                fontSize: "20px",
                borderRadius: "2px"
            }
        },
        countryCodeSelect: {
            "& .MuiSelect-select": {
                padding: "16px 0px",
                fontSize: "20px",
                fontWeight: 700,
                fontFamily: "Inter, 'sans-serif'",
                display: "flex",
                alignItems: "center",
                "&:focus": {
                    backgroundColor: "transparent"
                }
            },
            "& .MuiSelect-icon": {
                display: "none"
            }
        },
        phoneNumberWrapper: {
            display: "flex",
            alignItems: "center",
            gap: "5px",
            paddingLeft: "12px",
            borderBottom: "1px solid rgba(0, 0, 0, 0.5)"
        },
        separator: {
            height: "26px",
            width: "1px",
            backgroundColor: "#6F6F70"
        },
        phoneNumber: {
            width: "100%",
            "& input": {
                fontSize: "20px",
                fontWeight: 700,
                fontFamily: "Inter, 'sans-serif'",
                padding: "16px 12px 16px 3px",
                "&::placeholder": {
                    color: "rgba(0, 0, 0, 0.5)"
                }
            }
        },
        countryCodeMenu: {
            maxHeight: "260px",
            borderRadius: "7px",
            "& > ul": {
                padding: "0",
                "& li": {
                    fontFamily: "Inter, 'sans-serif'",
                    padding: "12px 9px",
                    fontSize: "15px",
                    "&:hover": {
                        backgroundColor: "#f1f1f1"
                    },
                    "& .country-flag": {
                        fontSize: "18px",
                        borderRadius: "2px",
                        marginRight: "5px"
                    },
                    "& .country-dial": {
                        color: "#6b6b6b",
                        marginLeft: "5px"
                    }
                }
            }
        },
        flexDisplaySU: {
            display: "flex",
            alignItems: "center",
            marginTop: "10px"
        },
        lockIcon: {
            height: "16px",
            width: "16px",
            marginRight: "5px"
        },
        lockTextSU: {
            fontSize: "13px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "16px",
            fontFamily: "Inter, 'sans-serif'"
        },
        textOne: {
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "14px",
            padding: "35px 0px 0px",
            fontFamily: "Inter, 'sans-serif'"
        },
        textTwoSU: {
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "14px",
            fontFamily: "Inter, 'sans-serif'"
        },
        centerTextSU: {
            color: "#000",
            fontSize: "20px",
            lineHeight: "30px",
            fontWeight: 700,
            paddingLeft: "20px",
            paddingRight: "20px"
        },
        centerTextSU1: {
            color: "#000",
            textAlign: "center",
            alignItems: "center",
            fontSize: "20px",
            lineHeight: "30px",
            fontWeight: 700,
            margin: "0 auto",
            fontFamily: "Inter, 'sans-serif'",
            overflowY: "auto",
            overflow: "visible",
            height: "auto",
            maxHeight: "none"
        },
        centerTextSU2: {
            color: "#000",
            fontSize: "20px",
            lineHeight: "30px",
            fontWeight: 700,
            paddingLeft: "10px",
            paddingRight: "35px",
            overflow: "scroll",
            position: "absolute",
            top: "50px"
        },
        mainContainer: {
            ...commonStyles.mobileContainer,
            padding: "30px 20px",
            overflowY: "auto",
            maxWidth: "450px",
        },
        buttonDisable: {
            ...commonStyles.mobileButton,
            width: "100%",
            alignSelf: "center",
            height: "54px",
            color: "#666",
            backgroundColor: "#cccccc",
            marginBottom: "70px",
            "&:hover": {
                color: "#666",
                backgroundColor: "#cccccc"
            }
        },
        buttonYellowDisable: {
            ...commonStyles.mobileButton,
            backgroundColor: "#7f6415",
            width: "100%",
            "&:hover": {
                color: "#ffffff",
                backgroundColor: "#7f6415",
            },
        },
        buttonPrimary: {
            ...commonStyles.mobileButton,
            width: "100%",
            alignSelf: "center",
            height: "56px",
            backgroundColor: "#000000",
            marginBottom: "70px"
        },
        buttonPrimaryThree: {
            ...commonStyles.mobileButton,
            width: "100%",
            alignSelf: "center",
            height: "56px",
            backgroundColor: "#000000",
            marginBottom: "20px"
        },
        buttonPrimarySignUp: {
            ...commonStyles.mobileButton,
            marginBottom: "20px",
            width: "80%",
            alignSelf: "center",
            height: "56px",
            backgroundColor: "#7f6415"
        },
        backIconBox: {
            margin: "10px 20px",
            position: "relative",
            right: "20px"
        },
        toggleLabel: {
            marginRight: "0px",
            "& span": {
                margin: "0px"
            }
        },
        thoughtsInput: {
            background: "transparent",
            border: "none",
            outline: "none",
            fontSize: "20px",
            fontWeight: 'bold',
            lineHeight: "30px",
            fontFamily: "Inter",
            width: "100%",
            textAlign: "center",
            color: "#000",
            maxHeight: "90px",
            padding: "0 8px",
            resize: "none",
            "&::placeholder": {
                fontSize: "18px",
                fontWeight: 'bold',
                textAlign: "center",
                color: "#000",
                opacity: 1
            },
            "&:focus::placeholder": {
                opacity: 0
            }
        },
        signInContainer: {
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginTop: "30px",
            color: "#0F172A",
            lineHeight: "24px",
            fontFamily: "Inter",
            fontWeight: 400,
            "&>span": {
                fontWeight: "700",
                lineHeight: "24px",
                fontFamily: "Inter",
                cursor: "pointer"
            }
        },
        secondStepMainBox: {
            overflow: "visible",
            height: "auto",
            maxHeight: "none"
        }
    });

export default withStyles(styles)(GuestSignup);
